import { z } from 'zod';
import { isUserPromptEvent, type AxonEvent } from './event.types.js';
import {
  AskUserQuestionDetailSchema,
  AskUserQuestionItemSchema,
  parseFileEnvelopes,
} from '@runloop/reflex-contract';

/**
 * A flat, chronological projection of an agent run's Axon event stream into the
 * conversation it represents: the user's prompts, the agent's text replies, the
 * tool calls it made, and any AskUserQuestion it asked (with the answer it
 * received). This is the same information the chat view renders, reduced to a
 * shape that is easy for another agent (or an MCP client) to read top to bottom.
 *
 * The builder is intentionally framework-agnostic and depends only on
 * `@reflex/shared` primitives, so both the server (the `get_agent_context` MCP
 * tool) and the web can consume it. It is a *projection* for consumption, not a
 * rendering model — it deliberately omits the streaming/diff/timing concerns the
 * React chat transform (`plugins/plugin-chat` `useSDKStream`) carries.
 */

/** Max characters of a tool's output text kept in the transcript. */
const MAX_TOOL_OUTPUT_CHARS = 2000;

const TruncationMarker = '… [truncated]';

function truncate(text: string, max = MAX_TOOL_OUTPUT_CHARS): string {
  return text.length > max ? text.slice(0, max) + TruncationMarker : text;
}

// --- Output schema ---

/** A file the user attached to a prompt (content is inlined for the agent, so only the chip metadata is kept here). */
export const TranscriptAttachmentSchema = z.object({
  name: z.string(),
  mimeType: z.string(),
});
export type TranscriptAttachment = z.infer<typeof TranscriptAttachmentSchema>;

/** A prompt the user (or another caller) sent to the agent. */
export const TranscriptUserPromptSchema = z.object({
  kind: z.literal('user_prompt'),
  text: z.string(),
  attachments: z.array(TranscriptAttachmentSchema).optional(),
  timestamp: z.number().optional(),
});

/** A text reply from the agent. */
export const TranscriptAgentMessageSchema = z.object({
  kind: z.literal('agent_message'),
  text: z.string(),
  timestamp: z.number().optional(),
});

/** The agent's internal reasoning. Only present when the transcript is built with `includeThinking`. */
export const TranscriptAgentThinkingSchema = z.object({
  kind: z.literal('agent_thinking'),
  text: z.string(),
  timestamp: z.number().optional(),
});

/** A tool the agent invoked, with its input and (when available) a truncated result. */
export const TranscriptToolCallSchema = z.object({
  kind: z.literal('tool_call'),
  name: z.string(),
  input: z.unknown().optional(),
  output: z.string().optional(),
  status: z.string().optional(),
  timestamp: z.number().optional(),
});

/** An AskUserQuestion the agent raised, plus the answer it received (if any). */
export const TranscriptAgentQuestionSchema = z.object({
  kind: z.literal('agent_question'),
  questions: z.array(AskUserQuestionItemSchema),
  /** Map of question text to the chosen option label (or free text). Absent when unanswered or declined. */
  answers: z.record(z.string(), z.string()).optional(),
  answered: z.boolean(),
  timestamp: z.number().optional(),
});

export const AgentTranscriptEntrySchema = z.discriminatedUnion('kind', [
  TranscriptUserPromptSchema,
  TranscriptAgentMessageSchema,
  TranscriptAgentThinkingSchema,
  TranscriptToolCallSchema,
  TranscriptAgentQuestionSchema,
]);
export type AgentTranscriptEntry = z.infer<typeof AgentTranscriptEntrySchema>;

export const AgentTranscriptSchema = z.object({
  agentId: z.string(),
  entries: z.array(AgentTranscriptEntrySchema),
});
export type AgentTranscript = z.infer<typeof AgentTranscriptSchema>;

// --- Parsing helpers ---

function isRecord(value: unknown): value is Record<string, unknown> {
  return value != null && typeof value === 'object' && !Array.isArray(value);
}

/**
 * Read an event payload as an object, tolerating the JSON-string form that can
 * occur when events cross Axon SSE / REST serialization boundaries. Mirrors the
 * chat view's `parsePayload` in `timeline-helpers.ts`.
 */
function parsePayload(raw: unknown): Record<string, unknown> {
  if (isRecord(raw)) return raw;
  if (typeof raw === 'string') {
    try {
      const parsed: unknown = JSON.parse(raw);
      if (isRecord(parsed)) return parsed;
    } catch {
      // not valid JSON — fall through
    }
  }
  return {};
}

/**
 * Claude SDK bridge events arrive prefixed with `turn.claude.` (e.g.
 * `turn.claude.result`). Strip the prefix so dispatch keys off the inner type,
 * matching `classifyEvent` in the chat view.
 */
function innerType(type: string): string {
  return type.startsWith('turn.claude.') ? type.slice('turn.claude.'.length) : type;
}

/** Concatenate every text block of a user-message payload (Claude `message.content[]`, ACP `prompt[]`, or string). */
function joinUserTextBlocks(payload: Record<string, unknown>): string {
  const message = payload.message;
  if (typeof message === 'string') return message;
  if (isRecord(message)) {
    const content = message.content;
    if (typeof content === 'string') return content;
    if (Array.isArray(content)) return joinTextBlocks(content);
  }

  const prompt = payload.prompt;
  if (typeof prompt === 'string') return prompt;
  if (Array.isArray(prompt)) return joinTextBlocks(prompt);

  const params = payload.params;
  if (isRecord(params) && Array.isArray(params.input)) return joinTextBlocks(params.input);

  if (typeof payload.text === 'string') return payload.text;
  return '';
}

/** Join the `text` field of every text-typed block in a content array. */
function joinTextBlocks(content: unknown[]): string {
  return content
    .filter((b): b is Record<string, unknown> => isRecord(b) && typeof b.text === 'string')
    .map((b) => b.text as string)
    .join('\n');
}

/** Stringify a tool result's `content` (string, or an array of `{ type: 'text', text }` blocks). */
function toolResultText(content: unknown): string {
  if (typeof content === 'string') return content;
  if (Array.isArray(content)) return joinTextBlocks(content);
  return '';
}

/** Coerce an AskUserQuestion answers map (values may be strings or arrays for multi-select) to `Record<string, string>`. */
function normalizeAnswers(input: Record<string, unknown>): Record<string, string> {
  const answers: Record<string, string> = {};
  for (const [question, value] of Object.entries(input)) {
    if (typeof value === 'string') answers[question] = value;
    else if (Array.isArray(value)) answers[question] = value.map((v) => String(v)).join(', ');
    else if (value != null) answers[question] = JSON.stringify(value);
  }
  return answers;
}

// --- Event handlers (mutate the entries array / index maps in place) ---

interface BuildContext {
  entries: AgentTranscriptEntry[];
  /** tool call id -> index in `entries`, so a later result/update can attach output + status. */
  toolCallById: Map<string, number>;
  /** AskUserQuestion request id -> index in `entries`, so a later control_response can fill in the answer. */
  questionByRequestId: Map<string, number>;
  /** Whether to emit the agent's reasoning as `agent_thinking` entries. */
  includeThinking: boolean;
}

/** Append `text` as an `agent_thinking` entry, folding into a contiguous one (ACP streams reasoning as chunks). */
function pushThinking(ctx: BuildContext, text: string, timestamp: number): void {
  if (!text) return;
  const last = ctx.entries[ctx.entries.length - 1];
  if (last && last.kind === 'agent_thinking') last.text += text;
  else ctx.entries.push({ kind: 'agent_thinking', text, timestamp });
}

/**
 * A user prompt is a protocol-specific prompt event, or a generic `message`
 * tagged `role: 'user'`. Protocol classification is shared with the chat and
 * server author-attribution paths so new protocol shapes cannot drift.
 */
function isTranscriptUserPromptEvent(
  event: AxonEvent,
  type: string,
  payload: Record<string, unknown>,
): boolean {
  if (isUserPromptEvent({ type, origin: event.origin })) return true;
  if (type === 'message') return payload.role === 'user';
  return false;
}

function handleUserPrompt(
  ctx: BuildContext,
  payload: Record<string, unknown>,
  timestamp: number,
): void {
  const raw = joinUserTextBlocks(payload);
  const { cleanedText, files } = parseFileEnvelopes(raw);
  const attachments = files.map((f) => ({ name: f.name, mimeType: f.mimeType }));
  // Skip pure-noise echoes that carry neither prose nor an attachment.
  if (!cleanedText && attachments.length === 0) return;
  ctx.entries.push({
    kind: 'user_prompt',
    text: cleanedText,
    ...(attachments.length > 0 ? { attachments } : {}),
    timestamp,
  });
}

/**
 * Claude `assistant` message: emit text blocks and `tool_use` blocks in order.
 * `thinking` blocks are emitted (in order) only when `includeThinking` is set.
 */
function handleClaudeAssistant(
  ctx: BuildContext,
  payload: Record<string, unknown>,
  timestamp: number,
): void {
  const message = payload.message;
  const content = (isRecord(message) ? message.content : undefined) ?? payload.content;
  if (!Array.isArray(content)) return;

  let pendingText: string[] = [];
  const flushText = () => {
    if (pendingText.length > 0) {
      ctx.entries.push({ kind: 'agent_message', text: pendingText.join('\n'), timestamp });
      pendingText = [];
    }
  };

  for (const block of content) {
    if (!isRecord(block)) continue;
    if (block.type === 'text' && typeof block.text === 'string') {
      pendingText.push(block.text);
    } else if (block.type === 'thinking') {
      if (ctx.includeThinking && typeof block.thinking === 'string') {
        flushText();
        pushThinking(ctx, block.thinking, timestamp);
      }
    } else if (block.type === 'tool_use') {
      flushText();
      const name = typeof block.name === 'string' ? block.name : 'unknown';
      const entry: AgentTranscriptEntry = { kind: 'tool_call', name, timestamp };
      if (block.input !== undefined) entry.input = block.input;
      ctx.entries.push(entry);
      if (typeof block.id === 'string') ctx.toolCallById.set(block.id, ctx.entries.length - 1);
    }
  }
  flushText();
}

/** Claude `user` event carrying `tool_result` blocks: attach output + status to the matching tool call. */
function handleClaudeToolResults(ctx: BuildContext, payload: Record<string, unknown>): void {
  const message = payload.message;
  const content = (isRecord(message) ? message.content : undefined) ?? payload.content;
  if (!Array.isArray(content)) return;

  for (const block of content) {
    if (!isRecord(block) || block.type !== 'tool_result') continue;
    const id = typeof block.tool_use_id === 'string' ? block.tool_use_id : undefined;
    if (!id) continue;
    const idx = ctx.toolCallById.get(id);
    if (idx === undefined) continue;
    const entry = ctx.entries[idx];
    if (entry.kind !== 'tool_call') continue;
    const output = toolResultText(block.content);
    if (output) entry.output = truncate(output);
    entry.status = block.is_error === true ? 'failed' : 'completed';
  }
}

/**
 * Claude turn-end `result` text. Usually echoes the final streamed assistant
 * message, so only surface it when it differs from the most recent agent
 * message — this captures content that never streamed as a block (e.g.
 * "Unknown command: /foo") without duplicating normal turns.
 */
function handleClaudeResult(
  ctx: BuildContext,
  payload: Record<string, unknown>,
  timestamp: number,
): void {
  const result = typeof payload.result === 'string' ? payload.result.trim() : '';
  if (!result) return;
  for (let i = ctx.entries.length - 1; i >= 0; i--) {
    const entry = ctx.entries[i];
    if (entry.kind === 'agent_message') {
      if (entry.text.trim() === result) return;
      break;
    }
  }
  ctx.entries.push({ kind: 'agent_message', text: result, timestamp });
}

/** Generic `message` event with an agent (non-user) role. */
function handleAgentMessage(
  ctx: BuildContext,
  payload: Record<string, unknown>,
  timestamp: number,
): void {
  const text = typeof payload.message === 'string' ? payload.message : '';
  if (text) ctx.entries.push({ kind: 'agent_message', text, timestamp });
}

/**
 * The ACP session-update body. ACP events may wrap the update under `update`
 * (a `SessionNotification`) or be the `SessionUpdate` directly; unwrap either.
 */
function acpUpdate(payload: Record<string, unknown>): Record<string, unknown> {
  return isRecord(payload.update) ? payload.update : payload;
}

function acpKind(type: string, update: Record<string, unknown>): string {
  if (typeof update.sessionUpdate === 'string') return update.sessionUpdate;
  return type;
}

function handleAcpEvent(
  ctx: BuildContext,
  type: string,
  payload: Record<string, unknown>,
  timestamp: number,
): void {
  const update = acpUpdate(payload);
  switch (acpKind(type, update)) {
    case 'agent_message_chunk': {
      const content = update.content;
      const text =
        isRecord(content) && content.type === 'text' && typeof content.text === 'string'
          ? content.text
          : '';
      if (!text) return;
      // Chunks stream incrementally — fold contiguous ones into a single message.
      const last = ctx.entries[ctx.entries.length - 1];
      if (last && last.kind === 'agent_message') last.text += text;
      else ctx.entries.push({ kind: 'agent_message', text, timestamp });
      return;
    }
    case 'agent_thought_chunk': {
      if (!ctx.includeThinking) return;
      const content = update.content;
      const text =
        isRecord(content) && content.type === 'text' && typeof content.text === 'string'
          ? content.text
          : '';
      pushThinking(ctx, text, timestamp);
      return;
    }
    case 'tool_call': {
      const name = typeof update.title === 'string' && update.title ? update.title : 'unknown';
      const entry: AgentTranscriptEntry = { kind: 'tool_call', name, timestamp };
      if (update.rawInput !== undefined) entry.input = update.rawInput;
      if (typeof update.status === 'string') entry.status = update.status;
      ctx.entries.push(entry);
      if (typeof update.toolCallId === 'string') {
        ctx.toolCallById.set(update.toolCallId, ctx.entries.length - 1);
      }
      return;
    }
    case 'tool_call_update': {
      const id = typeof update.toolCallId === 'string' ? update.toolCallId : undefined;
      if (!id) return;
      const idx = ctx.toolCallById.get(id);
      if (idx === undefined) return;
      const entry = ctx.entries[idx];
      if (entry.kind !== 'tool_call') return;
      if (typeof update.status === 'string') entry.status = update.status;
      if (entry.name === 'unknown' && typeof update.title === 'string' && update.title) {
        entry.name = update.title;
      }
      const output = Array.isArray(update.content) ? joinTextBlocks(update.content) : '';
      if (output) entry.output = truncate(output);
      return;
    }
    // `plan` and other ACP updates are omitted.
  }
}

/** Claude `control_request` for AskUserQuestion: push a question entry. Non-AskUserQuestion requests (interrupt, permission) are ignored. */
function handleControlRequest(
  ctx: BuildContext,
  payload: Record<string, unknown>,
  timestamp: number,
): void {
  const request = payload.request;
  if (!isRecord(request) || request.tool_name !== 'AskUserQuestion') return;
  const parsed = AskUserQuestionDetailSchema.safeParse(request.input);
  if (!parsed.success) return;
  ctx.entries.push({
    kind: 'agent_question',
    questions: parsed.data.questions,
    answered: false,
    timestamp,
  });
  if (typeof payload.request_id === 'string') {
    ctx.questionByRequestId.set(payload.request_id, ctx.entries.length - 1);
  }
}

/** Claude `control_response`: mark the matching AskUserQuestion answered and record the chosen answers (allow) — a deny leaves `answers` absent. */
function handleControlResponse(ctx: BuildContext, payload: Record<string, unknown>): void {
  const outer = isRecord(payload.response) ? payload.response : undefined;
  const requestId =
    (typeof payload.request_id === 'string' && payload.request_id) ||
    (outer && typeof outer.request_id === 'string' ? outer.request_id : null);
  if (!requestId) return;
  const idx = ctx.questionByRequestId.get(requestId);
  if (idx === undefined) return;
  const entry = ctx.entries[idx];
  if (entry.kind !== 'agent_question') return;

  entry.answered = true;
  const inner = outer && isRecord(outer.response) ? outer.response : undefined;
  if (inner && inner.behavior !== 'deny') {
    const updatedInput = isRecord(inner.updatedInput) ? inner.updatedInput : undefined;
    if (updatedInput && isRecord(updatedInput.answers)) {
      entry.answers = normalizeAnswers(updatedInput.answers);
    }
  }
}

/** Options controlling what {@link buildAgentTranscript} includes. */
export interface BuildAgentTranscriptOptions {
  /**
   * Emit the agent's internal reasoning as `agent_thinking` entries (Claude
   * `thinking` blocks, ACP `agent_thought_chunk`). Off by default — thinking is
   * verbose and usually not needed to understand what a run did.
   */
  includeThinking?: boolean;
}

/**
 * Reduce an agent's durable Axon event stream to a chronological transcript of
 * user prompts, agent messages, tool calls, and AskUserQuestion exchanges.
 * Handles both the Claude and ACP agent protocols. Pass `includeThinking` to
 * also surface the agent's reasoning.
 */
export function buildAgentTranscript(
  agentId: string,
  events: readonly AxonEvent[],
  options: BuildAgentTranscriptOptions = {},
): AgentTranscript {
  const ctx: BuildContext = {
    entries: [],
    toolCallById: new Map(),
    questionByRequestId: new Map(),
    includeThinking: options.includeThinking ?? false,
  };

  for (const event of events) {
    const type = innerType(event.type);
    const payload = parsePayload(event.payload);
    const timestamp = event.timestamp;

    if (isTranscriptUserPromptEvent(event, type, payload)) {
      handleUserPrompt(ctx, payload, timestamp);
      continue;
    }

    switch (type) {
      case 'assistant':
        handleClaudeAssistant(ctx, payload, timestamp);
        break;
      case 'user':
        handleClaudeToolResults(ctx, payload);
        break;
      case 'result':
        handleClaudeResult(ctx, payload, timestamp);
        break;
      case 'message':
        handleAgentMessage(ctx, payload, timestamp);
        break;
      case 'control_request':
        handleControlRequest(ctx, payload, timestamp);
        break;
      case 'control_response':
        handleControlResponse(ctx, payload);
        break;
      case 'session/update':
      case 'agent_message_chunk':
      case 'agent_thought_chunk':
      case 'tool_call':
      case 'tool_call_update':
        handleAcpEvent(ctx, type, payload, timestamp);
        break;
      default:
        break;
    }
  }

  return { agentId, entries: ctx.entries };
}
